import React, { useState, useEffect } from 'react';
import { Table, Button, Spin } from 'antd';
import dayjs from 'dayjs';
import SmartTable from './SmartTable';
import HOC from './HOC';
import useAuth from '../../hooks/useAuth';
const getEmployeeColumns = HOC('employee');
function isTimeRangeOverlap(start1, end1, start2, end2) {
  const toMinutes = t => {
    if (!t || typeof t !== 'string' || !/^\d{2}:\d{2}$/.test(t)) return NaN;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const s1 = toMinutes(start1), e1 = toMinutes(end1);
  const s2 = toMinutes(start2), e2 = toMinutes(end2);
  if ([s1, e1, s2, e2].some(isNaN)) return false;
  return s1 < e2 && s2 < e1;
}

const columns = [
  { title: 'Sl. no.', dataIndex: 'key', key: 'key', width: 60 },
  { title: 'Employee Name', dataIndex: 'name', key: 'name', width: 180 },
  { title: 'Employee ID', dataIndex: 'id', key: 'id', width: 120 },
  { title: 'Union', dataIndex: 'union', key: 'union', width: 120 },
  { title: 'SSN4', dataIndex: 'ssn4', key: 'ssn4', width: 100 },
  { title: 'Task', dataIndex: 'task', key: 'task', width: 120 },
];
const baseHeaderStyle = {
  backgroundColor: "#EAEEFF",
  color: "black",
  borderRight: "1px solid #d9d9d9",
  fontWeight: "bold",
};
const ScheduleStatus = ({
  assignDate,
  onOverride,
  successToAssign = 0,
  conflictToAssign = [],
  integrationResponse,
  scheduleOverridePermission,
  onResetStepper,
  onFiltersReady
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [successCount, setSuccessCount] = useState(successToAssign);
  const { userConfig } = useAuth();
  const [tablePageSize, setTablePageSize] = useState(100);

  useEffect(() => {
    const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
    if (!key || typeof window === "undefined") return;
    const stored = window.localStorage.getItem(key);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed) && parsed > 0) {
      setTablePageSize(parsed);
    }
  });

  const response = integrationResponse || [];
  const failedEmployees = (response || []).filter(item => item.status === 'failed');
  const failedColumns = [
    { title: 'Employee ID', dataIndex: 'empId', key: 'empId', width: 50 },
        { title: 'Employee Name', dataIndex: 'empName', key: 'empName', width: 60 },

    {
    title: 'Error Message',
    dataIndex: ['error', 'message'],
    key: 'errorMessage',
    width: 100,
    render: (text, record) =>
      text
        ? `${text.replace(/\.$/, '')}${record?.error?.errorCode ? ` (${record.error.errorCode})` : ''}.`
        : '-',
  },
  ];

  useEffect(() => {
    if (!Array.isArray(response)) return;
    if (failedEmployees.length === 0) {
      setSuccessCount(successToAssign);
      return;
    }

    const count = response.filter(
      item => item.status === 'success'
    ).length;

    setSuccessCount(count);
  }, [response]);


  const employeeColumns = getEmployeeColumns.map(col => ({
    ...col,
    onHeaderCell: () => ({
      style: baseHeaderStyle,
    }),
  }));

  const handleOverrideClick = async () => {
    setLoading(true);
    try {
      const selectedConflicts =
        selectedKeys.length > 0
          ? conflictToAssign.filter(c => selectedKeys.includes(String(c.employeeId)))
          : conflictToAssign;

      await onOverride(selectedConflicts);
      if (typeof onResetStepper === 'function'){
        onResetStepper();
      }
    } finally {
      setLoading(false);
    }
  };

  const exceptionEmployees = conflictToAssign.map((emp, idx) => ({
    ...emp,
    key: emp.employeeId || `k${idx + 1}`,
  }));

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys.map(k => String(k))),
    getCheckboxProps: () => ({
      defaultChecked: true,
    }),
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 12, padding: 32 }}>
      
      <div style={{ fontSize: 22, fontWeight: 600, textAlign: 'center', marginBottom: 32 }}>
        Schedule status
      </div>

      {/* SUCCESS SECTION */}
      {successCount > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#eaf2ff',
              color: '#1677ff',
              fontSize: 20,
              marginRight: 12,
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="10" fill="#eaf2ff" />
                <polyline points="5,11 9,15 15,7" style={{ fill: 'none', stroke: '#1677ff', strokeWidth: 2 }} />
              </svg>
            </span>
        <div style={{ fontWeight: 500, color: '#1677ff', fontSize: 16 }}>
              Success
            </div>
          </div>
        <div style={{ marginLeft: 44, color: '#222', fontSize: 15, marginBottom: 20 }}>
            {successCount} Employees were scheduled successfully on “{dayjs(assignDate)?.format("MM-DD-YYYY")}”.
       </div>
      </>
      )}
      {failedEmployees.length > 0 && (
        <div style={{ marginLeft: 44, justifyContent: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>Failed to schedule below employees: {failedEmployees.length}</div>
     <Table
            className="table-style"
            columns={failedColumns}
            dataSource={failedEmployees}
            pagination={false}
            scroll={failedEmployees.length > 10 ? { y: 205 } : false}
            bordered
            size="middle"
            style={{ width: '60%' }}
          />
        </div>
      )}
     {/* EXCEPTIONS SECTION */}
      {exceptionEmployees.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, marginTop: 24 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#ffeaea',
              color: '#ff4d4f',
              fontSize: 20,
              marginRight: 12,
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="10" fill="#ffeaea" />
                <line x1="7" y1="7" x2="13" y2="13" stroke="#ff4d4f" strokeWidth="2" />
                <line x1="13" y1="7" x2="7" y2="13" stroke="#ff4d4f" strokeWidth="2" />
              </svg>
            </span>
        <div style={{ fontWeight: 500, color: '#ff4d4f', fontSize: 16 }}>
              Exceptions
            </div>
          </div>
         <div style={{ marginLeft: 44, color: '#222', fontSize: 15, marginBottom: 16 }}>
            {exceptionEmployees.length} Employees are exceptions, click “Override Schedule” to unschedule these workmen and reassign them.
          </div>
        </>
      )}
        {/* TABLE FOR EXCEPTIONS */}
      {exceptionEmployees.length > 0 && (
        <SmartTable
          tableKey="schedule-status-exception-table"
          className="table-style"
          columns={employeeColumns}
          dataSource={exceptionEmployees}
          pagination={{
            pageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={(pagination, filters, sorter, extra) => {
            if (pagination && pagination.pageSize) {
              setTablePageSize(pagination.pageSize);
              const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
              if (key && typeof window !== "undefined") {
                window.localStorage.setItem(key, String(pagination.pageSize));
              }
            }
          }}
          // scroll={{ x: 'max-content', y: 455 }}
          scroll={exceptionEmployees?.length ? { x: "100%", y: 455 } : { y: 455 } }
          bordered
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          style={{ marginBottom: 32 }}
          onFiltersReady={onFiltersReady}
          disableFilterPersistence
        />
      )}
       {/* OVERRIDE BUTTON */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
        <Button
          type="primary"
          size="large"
          style={{ minWidth: 180 }}
          onClick={handleOverrideClick}
          disabled={selectedKeys.length === 0 || loading || !scheduleOverridePermission?.access}
          hidden={exceptionEmployees.length === 0}
        >
          {loading ? <Spin size="small" /> : "Override Schedule"}
        </Button>
      </div>
    </div>
  );
};

export default ScheduleStatus;


